import type {
  ApiResponse,
  PaginatedResponse,
  RechargePackage,
  Wallet,
  WalletTransaction,
} from '@klaro7/shared-types';

import api from '@/lib/api';
import { downloadBlob } from '@/lib/download';

export interface WalletTransactionQueryParams {
  page?: number;
  limit?: number;
  type?: string;
  startDate?: string;
  endDate?: string;
}

export async function getWallet(): Promise<Wallet> {
  const response = await api.get<ApiResponse<Wallet>>('/wallet');
  return response.data.data;
}

/**
 * Create a Stripe Checkout session for a wallet top-up.
 * `amount` must be one of the RECHARGE_PACKAGES amounts (10/30/50).
 * Returns the hosted Stripe Checkout URL to redirect the user to.
 */
export async function createRechargeCheckout(amount: number): Promise<{ url: string }> {
  const response = await api.post<ApiResponse<{ url: string }>>('/wallet/recharge/checkout', {
    amount,
  });
  return response.data.data;
}

/**
 * Confirm a recharge on return from Stripe Checkout (webhook fallback).
 * Credits the wallet idempotently server-side if the session is paid.
 * `amount`/`isFirstRecharge` describe the session's recharge even quando il
 * webhook ha già accreditato (credited: false).
 */
export async function confirmRecharge(
  sessionId: string
): Promise<{ credited: boolean; balance?: number; amount?: number; isFirstRecharge?: boolean }> {
  const response = await api.get<
    ApiResponse<{ credited: boolean; balance?: number; amount?: number; isFirstRecharge?: boolean }>
  >('/wallet/recharge/confirm', { params: { session_id: sessionId } });
  return response.data.data;
}

export async function getTransactions(
  params?: WalletTransactionQueryParams
): Promise<PaginatedResponse<WalletTransaction>> {
  const response = await api.get<PaginatedResponse<WalletTransaction>>('/wallet/transactions', {
    params,
  });
  return response.data;
}

export async function redeemFreeMinutes(): Promise<WalletTransaction> {
  const response = await api.post<ApiResponse<WalletTransaction>>('/wallet/redeem-free-minutes');
  return response.data.data;
}

export type WalletBonusSource =
  | 'welcome'
  | 'recharge_package'
  | 'first_recharge_promo'
  | 'cashback'
  | 'birthday'
  | 'referral_referrer'
  | 'referral_referee'
  | 'legacy'
  | 'manual';

export interface WalletBonus {
  id: string;
  source: WalletBonusSource;
  amount: number;
  remaining: number;
  currency: string;
  grantedAt: string;
  expiresAt: string | null;
}

/**
 * Active bonus credits (the "Borsellino Bonus" broken down by source),
 * each with its own expiry. Powers the "Le mie Promozioni" page.
 */
export async function getWalletBonuses(): Promise<WalletBonus[]> {
  const response = await api.get<ApiResponse<{ bonuses: WalletBonus[] }>>('/wallet/bonuses');
  return response.data.data.bonuses;
}

/**
 * Download the PDF receipt for a RECHARGE transaction as a blob and trigger
 * a browser download. Uses the shared axios client with `responseType: 'blob'`.
 */
export async function downloadTransactionReceipt(transactionId: string): Promise<void> {
  const response = await api.get(`/wallet/transactions/${transactionId}/receipt`, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'application/pdf' });
  downloadBlob(blob, `ricevuta-${transactionId}.pdf`);
}

export async function getRechargePackages(): Promise<{
  packages: RechargePackage[];
  promoRate: number;
  promoMaxRecharges: number;
}> {
  const response = await api.get<ApiResponse<{
    packages: RechargePackage[];
    promoRate: number;
    promoMaxRecharges: number;
  }>>('/wallet/recharge-packages');
  return response.data.data;
}
